import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ExceptionManager } from "Core/logManager";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { uploadFile } from "Core/data/FileUpload";
import { getApiErrorMessage } from "Core/data/Helpers";

const INIT_STATE = {
  //createDriver
  driverResponse: null,
  loading: false,
  error: null,

  //searchDrivers
  entity: { id: "drivers" },
  loading: false,
  total: 0,
  filters: {
    includeInactiveDrivers: true,
    unassigned: true,
    openSearch: "",
    tagIds: [],
    limit: 0,
    offset: 0,
  },
  registers: [],
  driverInfo: {},
  errorSearch: null,
  openFilters: false,

  //openCreateEdit
  openCreateEdit: false,
  driverGroup: null,
  supervisor: [],
  loadingSupervisor: false,
  errorSupervisor: null,

  //searchEntityLists
  entityCountryList: [],
  entityStateList: [],
  entityHosRuleSetList: [],
  loadingEntities: false,
  errorEntities: null,

  driverAvatar: null,

  //getDriverById
  driverById: null,
  loadingDriverById: false,

  //openDriverTransactionModal
  openTransaction: false,
  transactionError: false,
  transactions: [],
  fileUrl: null,
};

const route =
  "modules/driver/views/driverTable/components/createEditDriver/redux/slices";
const toAWSDate = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toISOString().slice(0, 10);
};

export const createDriver = createAsyncThunk(
  `${route}/createDriver`,
  async (payload, { rejectWithValue }) => {
    const {
      name,
      externalId,
      groupId,
      tagIds,
      timeZoneId,
      unitId,
      username,
      password,
      defaultTab,
      avatarFileId,
      status,
      supervisorId,
      licenseNumber,
      licenseCountry,
      licenseState,
      driverPin,
      hosRulesetIds,
      hosExempt,
      email,
      phone,
      notifyByEmail,
      notifyBySms,
      contactInfo = null,
      hosExemptComment = null,
      keyFobHex = null,
      specialDutyStatuses = null,
      empNum = null,
      licenseExpDate = null,
      hasVideoAccess = null,
    } = payload;

    try {
      let data = await clientMutation(
        `
        mutation createDriver (
            $name: String!,
            $externalId: String,
            $tagIds: [Int], 
            $timeZoneId: Int!, 
            $contactInfo: String, 
            $unitId: Int, 
            $username: String, 
            $password: String, 
            $defaultTab: String,
            $avatarFileId: Int,
            $driverTransitionFrom: String,
            $status: Int,
            $supervisorId: Int,
            $licenseNumber: String,
            $licenseCountry: String, 
            $licenseState: String,
            $licenseExpDate: AWSDate,
            $driverPin: String,
            $hosRulesetIds: String,
            $hosExempt: Boolean,
            $email: String,
            $phone: String,
            $notifyByEmail: Boolean,
            $notifyBySms: Boolean,
            $hosExemptComment: String,
            $keyFobHex: String,
            $specialDutyStatuses: String,
            $empNum: String,
            $hasVideoAccess: Boolean
        ) {
            createDriver (
                name: $name, 
                externalId: $externalId, 
                tagIds: $tagIds,
                timeZoneId: $timeZoneId, 
                unitId: $unitId, 
                username: $username, 
                password: $password, 
                defaultTab: $defaultTab,
                avatarFileId: $avatarFileId,
                driverTransitionFrom: $driverTransitionFrom,
                status: $status,
                supervisorId: $supervisorId,
                licenseNumber: $licenseNumber,
                licenseCountry: $licenseCountry,
                licenseState: $licenseState,
                licenseExpDate: $licenseExpDate,
                driverPin: $driverPin,
                hosRulesetIds: $hosRulesetIds,
                hosExempt: $hosExempt,
                email: $email,
                phone: $phone,
                notifyByEmail: $notifyByEmail,
                notifyBySms: $notifyBySms,
                contactInfo: $contactInfo,
                hosExemptComment: $hosExemptComment,
                keyFobHex: $keyFobHex,
                specialDutyStatuses: $specialDutyStatuses,
                empNum: $empNum,
                hasVideoAccess: $hasVideoAccess
            ) {
                externalId
                companyId
                driverId
                supervisorId
                supervisor {
                  firstName
                  lastName
                }
                tags { id label color path }
                unit { id label }
                timeZone { id title }
                fsmDriver { username defaultTab }
                licenseNumber
                licenseCountry
                licenseState
                driverPin
                hosRulesetIds
                hosExempt
                email
                phone
                notifyByEmail
                notifyBySms
                hosExemptComment
                specialDutyStatuses
                empNum
            }
        }
        `,
        {
          name,
          externalId: externalId || "",
          groupId,
          tagIds,
          timeZoneId,
          unitId,
          username,
          password,
          defaultTab,
          avatarFileId,
          driverTransitionFrom: "CloudWebSite",
          status,
          supervisorId: supervisorId || 0,
          licenseNumber,
          licenseCountry,
          licenseState,
          licenseExpDate: toAWSDate(licenseExpDate),
          driverPin,
          hosRulesetIds,
          hosExempt,
          email,
          phone,
          notifyByEmail,
          notifyBySms,
          contactInfo,
          hosExemptComment,
          keyFobHex,
          specialDutyStatuses,
          empNum,
          hasVideoAccess,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        return data?.createDriver;
      } else {
        throw "Error while creating driver";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "createDriver");
      return rejectWithValue(exc);
    }
  }
);

export const updateDriver = createAsyncThunk(
  `${route}/updateDriver`,
  async (payload, { rejectWithValue }) => {
    const {
      id,
      name,
      externalId,
      tagIds,
      timeZoneId,
      contactInfo,
      unitId,
      username,
      password,
      defaultTab,
      avatarFileId,
      tempAvatarUrl,
      status,
      supervisorId,
      licenseNumber,
      licenseCountry,
      licenseState,
      licenseExpDate,
      driverPin,
      hosRulesetIds,
      hosExempt,
      email,
      phone,
      notifyByEmail,
      notifyBySms,
      hosExemptComment,
      keyFobHex,
      specialDutyStatuses,
      empNum,
    } = payload;

    try {
      let data = await clientMutation(
        `
        mutation updateDriver (
            $id: Int!, 
            $name: String, 
            $externalId: String, 
            $tagIds: [Int],
            $timeZoneId: Int, 
            $contactInfo: String, 
            $unitId: Int, 
            $username: String, 
            $password: String, 
            $defaultTab: String,
            $avatarFileId: Int,
            $tempAvatarUrl: String,
            $driverTransitionFrom: String,
            $status: Int,
            $supervisorId: Int,
            $licenseNumber: String,
            $licenseCountry: String,
            $licenseState: String,
            $licenseExpDate: AWSDate,
            $driverPin: String,
            $hosRulesetIds: String,
            $hosExempt: Boolean,
            $hosExemptComment: String,
            $email: String,
            $phone: String,
            $notifyByEmail: Boolean,
            $notifyBySms: Boolean,
            $keyFobHex: String,
            $specialDutyStatuses: String,
            $empNum: String
        ) {
            updateDriver (
                id: $id, 
                name: $name, 
                externalId: $externalId, 
                tagIds: $tagIds,
                timeZoneId: $timeZoneId, 
                contactInfo: $contactInfo, 
                unitId: $unitId, 
                username: $username, 
                password: $password, 
                defaultTab: $defaultTab,
                avatarFileId: $avatarFileId,
                tempAvatarUrl: $tempAvatarUrl,
                driverTransitionFrom: $driverTransitionFrom,
                status: $status,
                supervisorId: $supervisorId,
                licenseNumber: $licenseNumber,
                licenseCountry: $licenseCountry,
                licenseState: $licenseState,
                licenseExpDate: $licenseExpDate,
                driverPin: $driverPin,
                hosRulesetIds: $hosRulesetIds,
                hosExempt: $hosExempt,
                email: $email,
                phone: $phone,
                notifyByEmail: $notifyByEmail,
                notifyBySms: $notifyBySms,
                hosExemptComment: $hosExemptComment,
                keyFobHex: $keyFobHex,
                specialDutyStatuses: $specialDutyStatuses,
                empNum: $empNum
            ) {
                name
                externalId
                contactInfo
                companyId
                driverId
                supervisorId
                supervisor {
                  firstName
                  lastName
                }
                tags { id label color path }
                unit { id label }
                timeZone { id title }
                fsmDriver { username defaultTab }
                licenseNumber
                licenseCountry
                licenseState
                driverPin
                hosRulesetIds
                hosExempt
                hosExemptComment
                email
                phone
                notifyByEmail
                notifyBySms
                empNum
                avatarFile {
                  id
                  repositoryId
                  fileKey
                }
            }
        }
    `,
        {
          id,
          name,
          externalId: externalId || "",
          tagIds,
          timeZoneId,
          contactInfo,
          unitId,
          username,
          password,
          defaultTab,
          avatarFileId,
          tempAvatarUrl,
          driverTransitionFrom: "CloudWebSite",
          status,
          supervisorId,
          licenseNumber,
          licenseCountry,
          licenseState,
          licenseExpDate: toAWSDate(licenseExpDate),
          driverPin,
          hosRulesetIds,
          hosExempt,
          email,
          phone,
          notifyByEmail,
          notifyBySms,
          hosExemptComment,
          keyFobHex,
          specialDutyStatuses,
          empNum,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        return data?.updateDriver;
      } else {
        throw "Error while updating driver";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "updateDriver");
      return rejectWithValue(exc);
    }
  }
);

export const searchEntityLists = createAsyncThunk(
  `${route}/searchEntityLists`,
  async (payload, { rejectWithValue }) => {
    const { terms, entityName } = payload;

    const body = {
      conditions: [
        {
          fields: ["entityName"],
          terms: terms,
        },
      ],
      limit: 100,
      offset: 0,
    };

    try {
      let data = await clientQuery(
        `query searchEntityLists ($conditions: [EntityListSearchInput], $sort: [EntityListSortInput], $limit: Int, $offset: Int) {
    searchEntityLists (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
        items {
            entityId
            entityName
            parentId
            label
            value1
            value2
        }
        total
    }
}`,
        body,
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        const {
          searchEntityLists: { items, total },
        } = data;
        return { items, total, entityName };
      } else {
        throw "Error while getting entities";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "searchEntityLists");
      return rejectWithValue(exc);
    }
  }
);

export const createDriverAvatar = createAsyncThunk(
  `${route}/createDriverAvatar`,
  async (payload, { rejectWithValue }) => {
    const { file, fileKeyDelete } = payload;

    try {
      if (fileKeyDelete) {
        const mutation = `mutation deleteAttachment ($id: Int!) {deleteAttachment (id: $id)}`;
        await clientMutation(mutation, { id: fileKeyDelete }, endpoints.GRAPHQL_GENERAL_V2);
      }

      if (file) {
        let extension = file?.name.split(".").pop();
        let presignedUrlResponse = await clientQuery(
          `query(
            $repositoryCode: String
            $fileExtension: String
          ) {
            Data: getPresignedUrl(
              repositoryCode: $repositoryCode,
              fileExtension: $fileExtension
            ) {
              repositoryCode
              fileKey
              url
            }
          }`,
          {
            repositoryCode: endpoints.REPOSITORY_CODE,
            fileExtension: extension,
          },
          endpoints.GRAPHQL_GENERAL_V2
        );

        if (!presignedUrlResponse || !presignedUrlResponse.Data) {
          throw "There are a problem uploading the File";
        }

        await uploadFile(presignedUrlResponse.Data.url, file);

        let registerAttachmentResult = await clientMutation(
          `mutation ($repositoryCode: String!, $fileKey: String!, $originalName: String){
            attachment: registerAttachment(repositoryCode: $repositoryCode, fileKey: $fileKey, originalName: $originalName){
              id
              repositoryId
              repositoryType
              repositoryCode
              repositoryPath
              fileKey
              originalName
            }
          }`,
          {
            repositoryCode: presignedUrlResponse.Data.repositoryCode,
            fileKey: presignedUrlResponse.Data.fileKey,
            originalName: file?.name,
          },
          endpoints.GRAPHQL_GENERAL
        );

        if (registerAttachmentResult) {
          return registerAttachmentResult.attachment;
        } else {
          throw "There are a problem register the File";
        }
      } else {
        return { id: null, fileKey: fileKeyDelete };
      }
    } catch (exc) {
      ExceptionManager(
        exc,
        "modules/settings/views/driver/redux/sagas",
        "createDriverAvatar",
        true
      );
      return rejectWithValue(exc);
    }
  }
);

export const getUrlFileAttachment = createAsyncThunk(
  `${route}/getUrlFileAttachment`,
  async (payload, { rejectWithValue }) => {
    const { id } = payload;
    try {
      const data = await clientQuery(
        `query (
          $id: Int!
        ){
          downloadAttachmentUrl: downloadAttachmentUrl(
            id: $id
          )
        }`,
        {
          id
        },
        endpoints.GRAPHQL_GENERAL,
      );

      if (data && data.downloadAttachmentUrl) {
        return data.downloadAttachmentUrl;
      } else {
        throw 'There are not url attachment';
      }
    } catch (exc) {
      ExceptionManager(
        exc,
        'modules/settings/views/driver/redux/sagas',
        'getUrlFileAttachment',
      );
      return rejectWithValue(exc);
    }
  }
);

export const searchDriverTransaction = createAsyncThunk(
  `${route}/searchDriverTransaction`,
  async (payload, { rejectWithValue }) => {
    const {
      transactionId,
      nextToken
    } = payload;

    try {
      const query = `
        query searchTransactions ($transactionId: String!, $nextToken: String) {
          searchTransactions (transactionId: $transactionId, nextToken: $nextToken) {
            items {
              Id
              TransactionId
              Type
              State
              ErrorMessage
              NewId
              Data
              CreatedBy
              CreatedOn
              ProcessedOn
            }
            nextToken
          }
        }
      `;

      const response = await clientQuery(
        query,
        {
          transactionId,
          nextToken
        },
        endpoints.GRAPHQL_GENERAL
      );

      if (!response || !response.searchTransactions) {
        throw "Error while getting Transactions";
      }

      return { items: response.searchTransactions, error: false };
    } catch (exc) {
      ExceptionManager(
        exc,
        "modules/settings/views/driver/redux/sagas",
        "searchDriverTransaction"
      );
      return rejectWithValue({ items: [], error: true });
    }
  }
);

// Thunk to open the create/edit modal and fetch driver data if editing
export const openDriverCreateEditModalWithData = createAsyncThunk(
  `${route}/openDriverCreateEditModalWithData`,
  async (payload, { dispatch, rejectWithValue }) => {
    const { open, driverGroup } = payload;
    
    // First, open the modal
    dispatch(openDriverCreateEditModal({ open, driverGroup }));
    
    // If we have a driver to edit (driverGroup has id), fetch the driver data
    if (open && driverGroup?.id) {
      try {
        // Convert string id to integer for getDriverById
        const driverId = parseInt(driverGroup.id);
        const driverResult = await dispatch(getDriverById({ id: driverId }));
        return driverResult.payload;
      } catch (error) {
        return rejectWithValue(error);
      }
    }
    
    return null;
  }
);

// Thunk to search the specific driver by ID, bring only the info needed for editing
export const getDriverById = createAsyncThunk(
  `${route}/getDriver`,
  async (payload, { rejectWithValue }) => {
    let { id } = (payload);
    id = parseInt(id);

    try {
      const query = 
      `
      query getDriver ($id: Int!) {
          data: getDriver (id: $id) {
              id
              name
              avatarFile {
                id
                repositoryId
                fileKey
              }
              avatarFileId
              externalId
              tagIds
              empNum
              driverPin
              licenseNumber
              licenseCountry
              licenseState
              licenseExpDate
              hosRulesetIds
              hosExempt
              hosExemptComment
              specialDutyStatuses
              unit {
                id
                label
              }
              timeZone {
                id
                title
              }
              idTimeZone
              supervisorId
              keyfob {
                hex
              }
              fsmDriver {
                username
                defaultTab
                lastConnected
                hasVideoAccess
              }
              email
              phone
              notifyByEmail
              notifyBySms
              status
              contactInfo
            }
          }
        `;      const response = await clientQuery(
        query,
        {
          id
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (!response || !response.data) {
        throw "Error while getting Driver";
      }

      return response.data;
    } catch (exc) {
      ExceptionManager(
        exc,
        "modules/settings/views/driver/redux/sagas",
        "getDriverById"
      );
      return rejectWithValue(null);
    }
  }
);

export const driverCreateEditSliceRedux = createSlice({
  name: `${route}/driverCreateEdit`,
  initialState: INIT_STATE,
  reducers: {
    setEntityName: (state, action) => {
      state.entityName = action.payload;
    },
    openDriverTransaction: (state, action) => {
      return {
        ...state,
        openTransaction: action.payload?.open
      };
    },
    openDriverCreateEditModal: (state, action) => {
      return {
        ...state,
        openCreateEdit: action.payload?.open,
        driverGroup: action.payload?.driverGroup,
        driverResponse: null,
      };
    },
    //added driverById and loadingDriverById to be cleared when modal is closed/edit is done
    clearReduxCreateEditDriver: (state, action) => {
      return {
        ...state,
        driverResponse: null,
        error: null,
        driverById: null,
        loadingDriverById: false,
        openCreateEdit: false,
        loading: false,
      };
    
    },
    reset: (state, action) => {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
    },
  },
  extraReducers: (builder) => {
    //createDriver
    builder.addCase(createDriver.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createDriver.fulfilled, (state, { payload }) => {
      if (!payload) {
        state.error = true;
      }
      state.loading = false;
      state.driverResponse = payload;
    });
    builder.addCase(createDriver.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = getApiErrorMessage(payload) || true;
    });

    //updateDriver
    builder.addCase(updateDriver.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateDriver.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.driverResponse = payload;
    });
    builder.addCase(updateDriver.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = getApiErrorMessage(payload) || true;
    });

    //searchEntityLists
    builder.addCase(searchEntityLists.pending, (state) => {
      state.loadingEntities = true;
    });
    builder.addCase(searchEntityLists.fulfilled, (state, { payload }) => {
      state.loadingEntities = false;

      let contries = payload.items.filter(item => item.entityName === 'COUNTRY');
      let states = payload.items.filter(item => item.entityName === 'STATE');
      let hosRuleSet = payload.items.filter(item => item.entityName === 'hosRuleSet');

      state.entityCountryList = contries;
      state.entityStateList = states;
      state.entityHosRuleSetList = hosRuleSet;
      state.totalEntities = payload.total;
    });
    builder.addCase(searchEntityLists.rejected, (state, { payload }) => {
      state.loadingEntities = false;
      state.errorEntities = payload;
    });

    //createDriverAvatar
    builder.addCase(createDriverAvatar.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createDriverAvatar.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.driverAvatar = payload;
    });
    builder.addCase(createDriverAvatar.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    //getUrlFileAttachment
    builder.addCase(getUrlFileAttachment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUrlFileAttachment.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.fileUrl = payload;
    });
    builder.addCase(getUrlFileAttachment.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    //searchDriverTransaction
    builder.addCase(searchDriverTransaction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(searchDriverTransaction.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.transactions = payload.items;
      state.transactionError = payload.error;
    });
    builder.addCase(searchDriverTransaction.rejected, (state) => {
      state.loading = false;
      state.transactions = [];
      state.transactionError = true;
    });

    //getDriverById
    builder.addCase(getDriverById.pending, (state) => {
      state.loadingDriverById = true;
    });
    builder.addCase(getDriverById.fulfilled, (state, { payload }) => {
      state.loadingDriverById = false;
      state.driverById = payload;

    });
    builder.addCase(getDriverById.rejected, (state) => {
      state.loadingDriverById = false;
      state.driverById = null;
    });

    //openDriverCreateEditModalWithData
    builder.addCase(openDriverCreateEditModalWithData.pending, (state) => {
      state.loadingDriverById = true;
    });
    builder.addCase(openDriverCreateEditModalWithData.fulfilled, (state, { payload }) => {
      state.loadingDriverById = false;
      // The modal is already opened by the internal dispatch
      // Driver data is already set by the getDriverById internal call
    });
    builder.addCase(openDriverCreateEditModalWithData.rejected, (state, { payload }) => {
      state.loadingDriverById = false;
      state.error = payload;
    });
  },
});


const { actions, reducer } = driverCreateEditSliceRedux;

export const { openDriverCreateEditModal, setEntityName, openDriverTransaction, clearReduxCreateEditDriver, reset } = actions;

export default reducer;
